import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { pwsService } from '../services/pwsService';

// PWS Content Data Structure (Database)
interface DatabasePWSSection {
  sectionNumber: string;
  title: string;
  description?: string;
  pages: DatabasePWSPage[];
  subsections: DatabasePWSSection[];
  metadata: {
    totalPages: number;
    totalSubsections: number;
    wordCount: number;
    version: string;
  };
  contractNumber: string;
}

interface DatabasePWSPage {
  pageNumber: string;
  title: string;
  content: DatabasePWSContent[];
  level: number;
}

interface DatabasePWSContent {
  title: string;
  content: string;
  contentType: string;
  order: number;
  metadata: {
    requirements: string[];
    deliverables: string[];
    acceptanceCriteria: string[];
  };
}

const PWSViewer: React.FC = () => {
  const { section, page } = useParams<{ section: string; page: string }>();
  const navigate = useNavigate();

  // Redirect to new ODOS PWS Viewer
  useEffect(() => {
    navigate('/odos/pws', { replace: true });
  }, [navigate]);

  // State for database content (kept for compatibility)
  const [dbSections, setDbSections] = useState<DatabasePWSSection[]>([]);
  const [dbSection, setDbSection] = useState<DatabasePWSSection | null>(null);
  const [dbPage, setDbPage] = useState<DatabasePWSPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useOriginal, setUseOriginal] = useState(false);

  // Edit state
  const [editingContent, setEditingContent] = useState<{pageId: string, blockIndex: number} | null>(null);
  const [editedText, setEditedText] = useState('');

  // Determine content source - use database primarily
  const sectionData = dbSection;
  const pageData = dbPage;

  const handleSectionChange = (newSection: string) => {
    navigate(`/pws/${newSection}`);
  };

  const handlePageChange = (newPage: string) => {
    navigate(`/pws/${section}/${newPage}`);
  };

  const getAvailableSections = () => {
    if (dbSections.length > 0) {
      return dbSections.map(s => s.sectionNumber);
    }
    return [];
  };

  const getAvailablePages = (sectionKey: string) => {
    if (dbSection && dbSection.pages) {
      return dbSection.pages.map(p => p.pageNumber);
    }
    return [];
  };

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      // Reset state before fetching
      setDbSection(null);
      setDbPage(null);
      setLoading(true);
      setError(null);

      try {
        if (!section && !page) {
          // Fetch all sections
          const response = await pwsService.getPWSSctions(useOriginal);
          setDbSections(response.data || []);
        } else if (section && !page) {
          // Fetch specific section
          const response = await pwsService.getPWSSection(section, useOriginal);
          setDbSection(response.data || null);
        } else if (section && page) {
          // Fetch specific section first
          const response = await pwsService.getPWSSection(section, useOriginal);
          const sectionData = response.data || null;
          setDbSection(sectionData);

          // Find the specific page
          if (sectionData && sectionData.pages) {
            const foundPage = sectionData.pages.find((p: DatabasePWSPage) => p.pageNumber === page);
            setDbPage(foundPage || null);
          }
        }
      } catch (err: any) {
        console.error('Database fetch error:', err.message);
        setError(`Failed to load PWS content: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [section, page, useOriginal]);

  const handleContentClick = (pageId: string, blockIndex: number, content: string) => {
    setEditingContent({pageId, blockIndex});
    setEditedText(content);
  };

  const handleSave = async () => {
    if (!editingContent || !editedText.trim()) return;
    
    try {
      await pwsService.updateContent(
        editingContent.pageId, 
        editingContent.blockIndex, 
        editedText
      );
      setEditingContent(null);
      // Refresh data
      setDbSections([]);
      setDbSection(null);
      setDbPage(null);
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading PWS content...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontFamily: 'Merriweather, serif' }}>
          Performance Work Statement (PWS)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          DHS USCIS Contract Requirements - ODOS-IV Implementation
        </Typography>

        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mt: 2 }}>
          <MuiLink component="button" variant="body1" onClick={() => navigate('/pws')} sx={{ color: 'primary.main' }}>
            PWS Home
          </MuiLink>
          {section && (
            <MuiLink component="button" variant="body1" onClick={() => navigate(`/pws/${section}`)} sx={{ color: 'primary.main' }}>
              Section {section}: {sectionData?.title}
            </MuiLink>
          )}
          {page && (
            <Typography variant="body1" color="text.primary">
              Page {page}: {pageData?.title}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>

      {/* Content Source Indicator */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={useOriginal ? "Original PWS (12 sections)" : "Structured PWS (6 sections)"}
            color={useOriginal ? "primary" : "success"}
            size="small"
          />
          <Button
            size="small"
            variant={useOriginal ? "contained" : "outlined"}
            onClick={() => {
              setUseOriginal(!useOriginal);
              setDbSections([]);
              setDbSection(null);
              setDbPage(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            {useOriginal ? "Switch to Structured" : "View Original"}
          </Button>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setDbSections([]);
              setDbSection(null);
              setDbPage(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/odos')}
          sx={{ borderColor: 'primary.main', color: 'primary.main' }}
        >
          Back to ODOS
        </Button>

        {!section && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {getAvailableSections().map((sec) => (
              <Chip
                key={sec}
                label={`Section ${sec}`}
                onClick={() => handleSectionChange(sec)}
                sx={{ cursor: 'pointer', bgcolor: 'primary.main', color: 'white' }}
              />
            ))}
          </Box>
        )}

        {section && !page && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All Sections"
              onClick={() => navigate('/pws')}
              variant="outlined"
              sx={{ cursor: 'pointer' }}
            />
            {getAvailablePages(section).map((pg: string) => (
              <Chip
                key={pg}
                label={`Page ${pg}`}
                onClick={() => handlePageChange(pg)}
                sx={{ cursor: 'pointer', bgcolor: 'primary.main', color: 'white' }}
              />
            ))}
          </Box>
        )}

        {page && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="All Sections"
              onClick={() => navigate('/pws')}
              variant="outlined"
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label={`Section ${section}`}
              onClick={() => navigate(`/pws/${section}`)}
              variant="outlined"
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        )}
      </Box>

      {/* Content Display */}
      {!section && (
        <Grid container spacing={3}>
          {dbSections.length > 0 ? (
            // Database sections
            dbSections.map((sec) => (
              <Grid item xs={12} md={6} lg={4} key={sec.sectionNumber}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => handleSectionChange(sec.sectionNumber)}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                      Section {sec.sectionNumber}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      {sec.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sec.metadata?.totalPages || 0} pages • {sec.metadata?.wordCount || 0} words
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contract: {sec.contractNumber}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                No PWS sections found in database. Please ensure the import was completed successfully.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {section && !page && sectionData && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
            Section {section}: {sectionData.title}
          </Typography>
          {sectionData.description && (
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              {sectionData.description}
            </Typography>
          )}
          <Grid container spacing={3}>
            {/* Display pages for this section */}
            {sectionData.pages && sectionData.pages.map((page: DatabasePWSPage) => (
              <Grid item xs={12} md={6} lg={4} key={page.pageNumber}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => handlePageChange(page.pageNumber)}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                      Page {page.pageNumber}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      {page.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Level {page.level} • {page.content.length} content blocks
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`${page.content.reduce((total, c) => total + c.metadata.requirements.length, 0)} Requirements`}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label={`${page.content.reduce((total, c) => total + c.metadata.deliverables.length, 0)} Deliverables`}
                        size="small"
                        color="secondary"
                      />
                      <Chip
                        label={`${page.content.reduce((total, c) => total + c.metadata.acceptanceCriteria.length, 0)} Criteria`}
                        size="small"
                        color="success"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Display subsections if they exist */}
            {sectionData.subsections && sectionData.subsections.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                  Subsections
                </Typography>
                <Grid container spacing={2}>
                  {sectionData.subsections.map((sub: any) => (
                    <Grid item xs={12} md={6} key={sub.sectionNumber}>
                      <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => handleSectionChange(sub.sectionNumber)}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                            Section {sub.sectionNumber}: {sub.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {sub.pageCount} pages
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {page && pageData && (
        <Box>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
            {pageData.title}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Display content blocks */}
              {pageData.content && pageData.content.map((contentBlock: DatabasePWSContent, index: number) => (
                <Paper key={index} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {contentBlock.title}
                  </Typography>
                  <Box sx={{ '& > *:not(:last-child)': { mb: 2 } }}>
                    {contentBlock.content.split('\n\n').map((paragraph: string, pIndex: number) => {
                      const trimmed = paragraph.trim();
                      
                      // Check if this starts with a bold item (e.g., **a)** or **aa)**)
                      const boldMatch = trimmed.match(/^\*\*([a-z]+\))\*\*(.*)$/);
                      if (boldMatch) {
                        const boldPart = boldMatch[1];
                        const restPart = boldMatch[2].trim();
                        
                        return (
                          <Typography key={pIndex} variant="body1" sx={{ mb: 2, textAlign: 'justify' }}>
                            <strong style={{ color: '#003366', fontSize: '1.1em' }}>{boldPart}</strong>
                            {restPart && <span> {restPart}</span>}
                          </Typography>
                        );
                      }
                      
                      // Check for inline bold text (e.g., text with **word** in the middle)
                      if (trimmed.includes('**')) {
                        const parts = trimmed.split(/(\*\*.*?\*\*)/g);
                        return (
                          <Typography key={pIndex} variant="body1" sx={{ mb: 2, textAlign: 'justify' }}>
                            {parts.map((part, i) => {
                              const inlineBold = part.match(/^\*\*(.*?)\*\*$/);
                              if (inlineBold) {
                                return <strong key={i} style={{ color: '#003366' }}>{inlineBold[1]}</strong>;
                              }
                              return <span key={i}>{part}</span>;
                            })}
                          </Typography>
                        );
                      }
                      
                      // Regular paragraph
                      return (
                        <Typography key={pIndex} variant="body1" sx={{ mb: 2, textAlign: 'justify' }}>
                          {trimmed}
                        </Typography>
                      );
                    })}
                  </Box>
                  {editingContent && editingContent.blockIndex === index ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => handleContentClick(page, index, contentBlock.content)}
                      sx={{ mt: 2, textTransform: 'none' }}
                    >
                      Edit
                    </Button>
                  )}
                </Paper>
              ))}
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Display all requirements from all content blocks */}
              {pageData.content && pageData.content.some(c => c.metadata.requirements.length > 0) && (
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Requirements
                  </Typography>
                  <List dense>
                    {pageData.content.flatMap(c => c.metadata.requirements).map((req: string, index: number) => {
                      // Parse bold markdown in requirements
                      const renderRequirement = (text: string) => {
                        if (text.includes('**')) {
                          const parts = text.split(/(\*\*.*?\*\*)/g);
                          return parts.map((part, i) => {
                            const boldMatch = part.match(/^\*\*(.*?)\*\*$/);
                            if (boldMatch) {
                              return <strong key={i} style={{ color: '#003366' }}>{boldMatch[1]}</strong>;
                            }
                            return <span key={i}>{part}</span>;
                          });
                        }
                        return text;
                      };
                      
                      return (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <strong>{index + 1}.</strong> {renderRequirement(req)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              )}

              {/* Display all deliverables from all content blocks */}
              {pageData.content && pageData.content.some(c => c.metadata.deliverables.length > 0) && (
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Deliverables
                  </Typography>
                  <List dense>
                    {pageData.content.flatMap(c => c.metadata.deliverables).map((deliverable: string, index: number) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              • {deliverable}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Display all acceptance criteria from all content blocks */}
              {pageData.content && pageData.content.some(c => c.metadata.acceptanceCriteria.length > 0) && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Acceptance Criteria
                  </Typography>
                  <List dense>
                    {pageData.content.flatMap(c => c.metadata.acceptanceCriteria).map((criteria: string, index: number) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              ✓ {criteria}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Grid>
          </Grid>

          {/* Navigation between pages */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/pws/${section}`)}
              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
            >
              Back to Section {section}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {(() => {
                const pages = getAvailablePages(section!);
                const currentIndex = pages.indexOf(page);
                const prevPage = currentIndex > 0 ? pages[currentIndex - 1] : null;
                const nextPage = currentIndex < pages.length - 1 ? pages[currentIndex + 1] : null;

                return (
                  <>
                    {prevPage && (
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => handlePageChange(prevPage)}
                        sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                      >
                        Page {prevPage}
                      </Button>
                    )}
                    {nextPage && (
                      <Button
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => handlePageChange(nextPage)}
                        sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                      >
                        Page {nextPage}
                      </Button>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
          {editingContent && (
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ mt: 2 }}
            >
              Save
            </Button>
          )}
        </Box>
      )}

      {section && !sectionData && (
        <Alert severity="error">
          Section {section} not found in PWS content.
        </Alert>
      )}

      {page && !pageData && (
        <Alert severity="error">
          Page {page} not found in Section {section}.
        </Alert>
      )}
    </Container>
  );
};

export default PWSViewer;
